import type { NextRouter } from 'next/router';
import { isBuildCurrent } from './buildVersion';
import { isNewApp } from './newAppShell';

/**
 * Patient-facing routes (ordered by how likely the next tap is) whose page bundles (JS modules, CSS, skeleton code) are worth having on the device
 * before the user taps. `router.prefetch` on the pages router fetches only the static, content-hashed
 * chunks of a page — never its data (getServerSideProps / API) — so nothing here goes stale or personal.
 */
const WARM_ROUTES = [
  // Core flow: search → profile → booking → payment → receipt
  '/s/[[...params]]',
  '/dr/[slug]',
  '/center/[slug]',
  '/booking/[slug]',
  '/booking/finalize',
  '/factor/[centerId]/[bookId]',
  '/factor/v2/[slug]/[service_id]',
  '/factor/v3',
  '/receipt/[centerId]/[bookId]',
  '/invoice/[centerId]/[bookId]',
  '/booking-queue/checkout',
  '/booking-queue/receipt',
  '/jabaz/checkout',
  '/jabaz/receipt',
  // Patient account
  '/patient',
  '/patient/appointments',
  '/patient/bookmarks',
  '/patient/profile',
  '/patient/queue',
  '/patient/reviews',
  '/patient/subuser',
  '/patient/premium',
  '/patient/contribute',
  '/patient/contribute/menu',
  '/patient/contribute/center',
  '/return-visit/[slug]',
  '/chat',
  '/chat-recovery/[bookId]',
  '/ravi/[user_id]',
  '/online-visit-suggestion',
  '/login',
  '/login/forgot',
  // Static / informational
  '/',
  '/faq',
  '/about-us',
  '/contact-us',
  '/privacy-policy',
  '/rate-us',
];

let warmed = false;

const shouldSkip = () => {
  const connection = (navigator as any).connection;
  if (!connection) return false;
  return Boolean(connection.saveData) || /(^|-)2g$/.test(connection.effectiveType ?? '');
};

const whenIdle = (task: () => void) => {
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(task, { timeout: 10000 });
  else window.setTimeout(task, 4000);
};

/**
 * Call once the first screen is interactive. Does nothing outside the new app (the website is left alone). Prefetches the main routes one at a time in idle time
 * (so it never competes with the current page) and only once per page load.
 */
export const warmAppShell = (router: Pick<NextRouter, 'prefetch'>) => {
  if (typeof window === 'undefined' || warmed || process.env.NODE_ENV !== 'production' || !isNewApp() || shouldSkip()) return;
  warmed = true;

  const queue = [...WARM_ROUTES];
  const next = () => {
    const route = queue.shift();
    if (!route) return;
    Promise.resolve(router.prefetch(route))
      .catch(() => undefined)
      .then(() => whenIdle(next));
  };
  whenIdle(() => {
    void isBuildCurrent().then(current => current && next());
  });
};
